<template>
  <div class="min-h-screen bg-[#F9FAFB] pb-24 px-4 pt-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-lg font-normal text-gray-900">
        {{ t("settings.title") }}
      </h1>
    </div>

    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <img
          :src="avatarSrc"
          class="w-12 h-12 rounded-full object-cover"
          alt="avatar"
          @error="onAvatarError"
        >
        <div>
          <p class="font-semibold text-sm text-gray-900 leading-tight">
            {{ profile.name || "—" }}
          </p>
          <p class="text-xs text-gray-500">
            {{ profile.email || "—" }} <span class="mx-1">•</span>
            {{ t("settings.user.level") }}
          </p>
        </div>
      </div>

      <div
        v-if="isLogin"
        class="relative"
      >
        <img
          src="@/assets/Badge1.png"
          class="w-11 h-11"
        >
      </div>
    </div>

    <div class="space-y-8">
      <div>
        <MenuRow
          icon="fas fa-user"
          :label="isLogin ? t('settings.menu.editProfile') : 'Login'"
          @click="goToUrl(isLogin ? '/edit-profile' : '/login')"
        />
        <MenuRow
          v-if="isLogin"
          icon="fas fa-key"
          :label="t('settings.menu.changePassword')"
          @click="goToUrl('/change-password')"
        />
        <MenuRow
          v-if="isLogin"
          icon="fas fa-crown"
          :label="t('settings.menu.subscribe')"
          @click="goToUrl('/subscribe')"
        />
      </div>

      <div v-if="isLogin">
        <p class="text-xs text-gray-400 font-semibold mb-3">
          {{ t("settings.preferences.title") }}
        </p>

        <!-- <MenuRow icon="fas fa-flag" label="Report Quiz" @click="goToUrl('/reports')" /> -->
        <MenuRow
          icon="fas fa-credit-card"
          :label="t('settings.preferences.paymentMethod')"
          @click="goToUrl('/payment-method')"
        />
        <MenuRow
          icon="far fa-bookmark"
          :label="t('settings.preferences.saved')"
          @click="goToUrl('/saved-quiz')"
        />
        <MenuRow
          icon="fas fa-bookmark"
          :label="t('settings.preferences.reviewBookmarks')"
          @click="goToUrl('/review-bookmarks')"
        />
        <MenuRow
          icon="fas fa-box-archive"
          :label="t('settings.preferences.archived')"
          @click="goToUrl('/archived-quiz')"
        />
        <MenuRow
          icon="far fa-bell"
          :label="t('settings.preferences.notifications')"
          @click="goToUrl('/notification-mobile')"
        />

        <div
          class="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer"
          @click="goToUrl('/language-setting-m')"
        >
          <div class="flex items-center gap-3">
            <i class="fas fa-globe text-lg text-gray-700 w-6 text-center" />
            <span class="text-sm font-medium text-gray-900">{{
              t("settings.preferences.languages")
            }}</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="text-sm text-gray-500">{{ languageDisplay }}</span>
            <i class="fas fa-chevron-down text-gray-400 text-xs" />
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs text-gray-400 font-semibold mb-3">
          {{ t("settings.resources.title") }}
        </p>

        <MenuRow
          icon="far fa-life-ring"
          :label="t('settings.resources.helpSupport')"
          @click="goToUrl('/help-support')"
        />
        <MenuRow
          icon="far fa-circle-question"
          :label="t('settings.resources.about')"
          @click="goToUrl('/about-mobile')"
        />
      </div>
    </div>
  </div>

  <BottomBarNavigation />
</template>

<script setup>
import BottomBarNavigation from "@/components/base/BottomBarNavigation.vue";
import { useAuthStore } from "@/stores/authStore";
import { GetProfile } from "@/api/settingApi";
import { useI18n } from "vue-i18n";
import {
  onMounted,
  ref,
  watchEffect,
  defineComponent,
  h,
  watch,
  computed,
} from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import DefaultAvatar from "@/assets/Avatar.png";
import { useLanguageStore } from "@/stores/languageStore";

const { t } = useI18n();
const router = useRouter();
const auth = useAuthStore();
const { isLoggedIn, userId } = storeToRefs(auth);
const isLogin = isLoggedIn;

const profile = ref({});
const avatarSrc = ref(DefaultAvatar);
const languageStore = useLanguageStore();
const { locale } = storeToRefs(languageStore);

const languageDisplay = computed(() => {
  const code = (locale.value || "").toLowerCase();
  switch (code) {
    case "ms-my":
      return "Malaysia";
    case "en-gbr":
      return "English";
    default:
      return code || "English";
  }
});

function resolveAvatarSrc(filePath) {
  if (!filePath || typeof filePath !== "string") return DefaultAvatar;
  try {
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const base =
      import.meta.env.VITE_ASSET_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      "";
    if (!base) return DefaultAvatar;
    const cleanBase = String(base).replace(/\/+$/, "");
    const cleanPath = String(filePath).replace(/^\/+/, "");
    return `${cleanBase}/${cleanPath}`;
  } catch {
    return DefaultAvatar;
  }
}

function onAvatarError(e) {
  const img = e?.target;
  if (img && img.src !== DefaultAvatar) img.src = DefaultAvatar;
}

watchEffect(() => {
  const fp = profile.value?.filePath;
  avatarSrc.value = resolveAvatarSrc(fp);
});

const MenuRow = defineComponent({
  name: "MenuRow",
  props: {
    icon: { type: String, required: true },
    label: { type: String, required: true },
  },
  emits: ["click"],
  setup(props, { emit }) {
    return () =>
      h(
        "div",
        {
          class:
            "flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer",
          onClick: () => emit("click"),
        },
        [
          h("div", { class: "flex items-center gap-3" }, [
            h("i", {
              class: `${props.icon} text-lg text-gray-700 w-6 text-center`,
            }),
            h(
              "span",
              { class: "text-sm font-medium text-gray-900" },
              props.label
            ),
          ]),
          h("i", { class: "fas fa-chevron-right text-gray-400 text-xs" }),
        ]
      );
  },
});

async function handleGetProfile() {
  try {
    if (!userId.value) {
      profile.value = {};
      return;
    }
    const response = await GetProfile(userId.value);
    profile.value = response || {};
  } catch {
    profile.value = {};
  }
}

onMounted(() => {
  auth.hydrateAndListenStorage();
  handleGetProfile();
});

watch(
  isLogin,
  (v) => {
    if (v) handleGetProfile();
    else profile.value = {};
  },
  { immediate: true }
);

function goToUrl(dir) {
  router.push(dir);
}
</script>

<style scoped></style>
